import React, { useRef, SyntheticEvent, useState } from 'react';

import {
    AudioPlayer,
    Button,
    GradientProgressBar,
    IconMusicNotePlus,
    leftNavConstants,
} from '@trqk/components';

import {
    constants,
    getFileInputAccept,
    getUseUploadTrackTaskId,
    uploadTrackTaskIsActive,
} from '@trqk/utils';

import { DetectionBadge } from '.';

import { useUploadTracks } from '../../../../hooks';
import { ManageDetectionsLink } from '../../../nav';

const { WIDTHS } = leftNavConstants;
const detectionsBadgeWidth = 132;
const detectionsBadgeLeftPadding = 10;
const elementHeight = 70;
const elementWidth =
    WIDTHS.MAIN_XL - detectionsBadgeWidth - detectionsBadgeLeftPadding;
const { SUPPORTED_AUDIO_FILE_TYPES } = constants;
const audioFileInputAccept = getFileInputAccept(SUPPORTED_AUDIO_FILE_TYPES);

interface AudioUploadOrPlayerProps {
    /**
     * location of audio file for this work in an s3 bucket
     */
    assetUrl: Maybe<string>;
    /**
     * Whether detection is active for this work
     */
    detectionIsOn: boolean;
    /**
     * Method called when a new trqkAudio is created
     */
    onCreateTrqkAudio(trqkAudio: TrqkAudio | null): void;
    /**
     * The trqkSong.percent_shares
     */
    percentShares: ProEntityPercentShare[] | null;
    /**
     * The trqkSong.id
     */
    trqkSongId: Maybe<string>;
    /**
     * The trqkSong.title
     */
    title: string;
    /**
     * The trqkSong.work_ids
     */
    workIds: string[];
}

/**
 * Based upon the current data for a work, shows either a button to allow audio file upload or an
 * audio file player with a waveform. By using the task system, the upload state for the work is
 * global; i.e. a user can start an upload from the Tracks page and then a progress bar will show up
 * here if the upload is still in progress.
 * @param props - see {@link AudioUploadOrPlayerProps}
 */
export const AudioUploadOrPlayer = ({
    assetUrl,
    detectionIsOn,
    onCreateTrqkAudio,
    percentShares,
    trqkSongId,
    title,
    workIds,
}: AudioUploadOrPlayerProps): JSX.Element => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const taskId = getUseUploadTrackTaskId(trqkSongId ?? '_empty_song_id_');
    const [task, setTask] = useState<UploadTrackTask | null>(null);

    const { cancel, handleFileForTrack } = useUploadTracks();
    const addFile = (): void => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    if (uploadTrackTaskIsActive(task)) {
        return (
            <div
                style={{
                    height: elementHeight,
                    width: elementWidth,
                }}
                className="flex items-center"
            >
                <GradientProgressBar
                    color="aqua"
                    error={task?.error ?? null}
                    handleCancel={(): void => {
                        cancel(taskId);
                    }}
                    height={50}
                    Icon={IconMusicNotePlus}
                    label={task?.input?.filename ?? ''}
                    labelLines={1}
                    inProgressLabel="Uploading..."
                    progress={task?.progress ?? -1}
                    width={550}
                />
            </div>
        );
    }

    return (
        <>
            {assetUrl && (
                <div className="w-full flex justify-between items-center">
                    <AudioPlayer
                        url={assetUrl}
                        height={elementHeight}
                        width={elementWidth}
                    />
                    <ManageDetectionsLink>
                        <DetectionBadge
                            detectionIsOn={detectionIsOn}
                            width={detectionsBadgeWidth}
                        />
                    </ManageDetectionsLink>
                </div>
            )}
            {!assetUrl && trqkSongId !== null && (
                <div
                    style={{
                        height: elementHeight,
                        width: elementWidth,
                    }}
                    className="flex items-center"
                >
                    <Button
                        color="primary"
                        autoWidth={true}
                        boxShadow={true}
                        handleClick={addFile}
                    >
                        <div className="flex mx-10 items-center">
                            <IconMusicNotePlus fillColor="white" />
                            <span className="ml-2 text-sm">
                                Upload Audio File
                            </span>
                        </div>
                        <input
                            accept={audioFileInputAccept}
                            className="visually-hidden"
                            id="audio-upload-or-player-file-input"
                            onChange={(e: SyntheticEvent): void => {
                                e.preventDefault();

                                if (fileInputRef.current) {
                                    const files = Array.from(
                                        fileInputRef.current?.files ?? [],
                                    );

                                    handleFileForTrack({
                                        file: files[0],
                                        trqkSongId,
                                        title,
                                        percentShares,
                                        taskId,
                                        onSuccess: (
                                            currentTask: UploadTrackTask,
                                        ): void => {
                                            const newTrqkAudio =
                                                currentTask?.output
                                                    ?.trqkAudio ?? null;

                                            onCreateTrqkAudio(newTrqkAudio);
                                        },
                                        onTaskUpdate: (
                                            currentTask: UploadTrackTask,
                                        ): void => {
                                            setTask(currentTask);
                                        },
                                        workIds,
                                    });

                                    // Reset the input's current value so it can be used again.
                                    fileInputRef.current.value = '';
                                }
                            }}
                            ref={fileInputRef}
                            type="file"
                        />
                    </Button>
                </div>
            )}
        </>
    );
};
